/** E-mailregistratie, inloggen en wachtwoord-reset via Firebase Auth. */
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { sendUserEmailVerification } from '@/services/authEmailVerification';
import { handlePostLogin } from '@/services/authServicePostLogin';

export const normalizeEmail = (email) => (email ?? '').trim().toLowerCase();

/**
 * @param {string} email
 * @param {string} password
 */
export const assertEmailPasswordPresent = (email, password) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) {
    const err = new Error('Empty email');
    err.code = 'auth/empty-email';
    throw err;
  }
  if (password === undefined || password === null || String(password).trim() === '') {
    const err = new Error('Empty password');
    err.code = 'auth/empty-password';
    throw err;
  }
  return normalizedEmail;
};

const PASSWORD_RESET_SURFACED_ERROR_CODES = new Set([
  'auth/too-many-requests',
  'auth/network-request-failed',
]);

export const signUpWithEmail = async (email, password) => {
  const normalizedEmail = assertEmailPasswordPresent(email, password);
  try {
    const result = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
    handlePostLogin(result.user, 'password');
    sendUserEmailVerification(result.user).catch((err) => {
      console.error(
        'AuthService: sendEmailVerification failed:',
        err?.code ?? err?.message,
      );
    });
    return result;
  } catch (error) {
    console.error('AuthService: Email sign-up failed:', error.code ?? error.message);
    throw error;
  }
};

/**
 * Requests a password-reset email. Surfaces only rate-limit and network errors;
 * other Firebase failures are handled neutrally to avoid account enumeration.
 * @param {string} email
 */
export const sendPasswordResetEmailForUser = async (email) => {
  const normalizedEmail = normalizeEmail(email);
  if (!normalizedEmail) {
    const err = new Error('Empty email');
    err.code = 'auth/empty-email';
    throw err;
  }
  try {
    await sendPasswordResetEmail(auth, normalizedEmail);
  } catch (error) {
    const code = error?.code;
    if (code && PASSWORD_RESET_SURFACED_ERROR_CODES.has(code)) {
      console.error('AuthService: Password reset failed:', code);
      throw error;
    }
    console.error(
      'AuthService: Password reset request handled neutrally:',
      code ?? error.message,
    );
  }
};

export const signInWithEmail = async (email, password) => {
  const normalizedEmail = assertEmailPasswordPresent(email, password);
  try {
    const result = await signInWithEmailAndPassword(auth, normalizedEmail, password);
    handlePostLogin(result.user, 'password');
    return result;
  } catch (error) {
    console.error('AuthService: Email sign-in failed:', error.code ?? error.message);
    throw error;
  }
};
